import React from 'react';
import { theme } from '../../styles/theme';

interface SpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  color?: string;
  variant?: 'default' | 'gradient' | 'pulse';
}

/** Animated loading spinner with multiple variants and sizes. */
export const Spinner: React.FC<SpinnerProps> = ({
  size = 'md',
  color,
  variant = 'gradient',
}) => {
  const sizeMap = {
    sm: 16,
    md: 24,
    lg: 40,
    xl: 56,
  };

  const dimension = sizeMap[size];
  const strokeWidth = size === 'sm' ? 2 : size === 'md' ? 3 : size === 'lg' ? 4 : 5;

  // Simple spinner with solid color
  if (variant === 'default' || color) {
    return (
      <>
        <div
          style={{
            width: dimension,
            height: dimension,
            border: `${strokeWidth}px solid ${theme.colors.border}`,
            borderTopColor: color || theme.colors.primary,
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }}
        />
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </>
    );
  }

  // Gradient spinner with brand colors
  if (variant === 'gradient') {
    const uniqueId = `gradient-spinner-${Math.random().toString(36).substr(2, 9)}`;

    return (
      <>
        <div
          style={{
            width: dimension,
            height: dimension,
            position: 'relative',
            animation: 'spin 1s linear infinite',
          }}
        >
          <svg
            width={dimension}
            height={dimension}
            viewBox={`0 0 ${dimension} ${dimension}`}
            style={{ display: 'block' }}
          >
            <defs>
              <linearGradient id={uniqueId} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={theme.colors.primary} />
                <stop offset="100%" stopColor={theme.colors.secondary} />
              </linearGradient>
            </defs>
            <circle
              cx={dimension / 2}
              cy={dimension / 2}
              r={(dimension - strokeWidth * 2) / 2}
              fill="none"
              stroke={theme.colors.border}
              strokeWidth={strokeWidth}
              opacity={0.3}
            />
            <circle
              cx={dimension / 2}
              cy={dimension / 2}
              r={(dimension - strokeWidth * 2) / 2}
              fill="none"
              stroke={`url(#${uniqueId})`}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeDasharray={`${(dimension - strokeWidth * 2) * Math.PI * 0.75} ${(dimension - strokeWidth * 2) * Math.PI * 0.25}`}
            />
          </svg>
        </div>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </>
    );
  }

  // Pulse variant with glowing effect
  if (variant === 'pulse') {
    return (
      <>
        <div
          style={{
            width: dimension,
            height: dimension,
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* Outer glow ring */}
          <div
            style={{
              position: 'absolute',
              width: dimension,
              height: dimension,
              borderRadius: '50%',
              background: 'transparent',
              border: `${strokeWidth}px solid transparent`,
              borderImage: theme.colors.gradientPrimary,
              animation: 'pulse-ring 1.5s ease-out infinite',
              opacity: 0.5,
            }}
          />
          {/* Inner spinning gradient */}
          <div
            style={{
              width: dimension * 0.7,
              height: dimension * 0.7,
              borderRadius: '50%',
              background: theme.colors.gradientPrimary,
              animation: 'pulse-core 1.5s ease-in-out infinite',
            }}
          />
        </div>
        <style>{`
          @keyframes pulse-ring {
            0% { transform: scale(0.8); opacity: 0.8; }
            50% { transform: scale(1.2); opacity: 0; }
            100% { transform: scale(0.8); opacity: 0; }
          }
          @keyframes pulse-core {
            0%, 100% { transform: scale(0.8); opacity: 0.8; }
            50% { transform: scale(1); opacity: 1; }
          }
        `}</style>
      </>
    );
  }

  return null;
};

/** Full-page loading overlay displayed while the wallet is initializing. */
export const WalletLoader: React.FC<{ message?: string }> = ({
  message = 'Loading your wallet...'
}) => {
  return (
    <>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: theme.spacing.xl,
          gap: theme.spacing.lg,
        }}
      >
        {/* Animated logo/icon */}
        <div
          style={{
            position: 'relative',
            width: '80px',
            height: '80px',
          }}
        >
          {/* Outer rotating ring */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              padding: '3px',
              background: theme.colors.gradientPrimary,
              WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
              WebkitMaskComposite: 'xor',
              maskComposite: 'exclude',
              animation: 'rotate-ring 2s linear infinite',
            }}
          />

          {/* Middle pulsing ring */}
          <div
            style={{
              position: 'absolute',
              inset: '10px',
              borderRadius: '50%',
              border: `2px solid ${theme.colors.border}`,
              animation: 'pulse-opacity 2s ease-in-out infinite',
            }}
          />

          {/* Inner gradient core */}
          <div
            style={{
              position: 'absolute',
              inset: '20px',
              borderRadius: '50%',
              background: theme.colors.gradientPrimary,
              animation: 'pulse-scale 2s ease-in-out infinite',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {/* Token icon */}
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M12 6v12" />
              <path d="M6 12h12" />
            </svg>
          </div>
        </div>

        {/* Loading text with gradient */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: theme.spacing.xs,
          }}
        >
          <p
            style={{
              fontSize: theme.fontSizes.sm,
              fontWeight: 500,
              background: theme.colors.gradientPrimary,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              margin: 0,
            }}
          >
            {message}
          </p>

          {/* Animated dots */}
          <div style={{ display: 'flex', gap: '4px' }}>
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  background: theme.colors.gradientPrimary,
                  animation: `bounce-dot 1.4s ease-in-out ${i * 0.16}s infinite`,
                }}
              />
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes rotate-ring {
          to { transform: rotate(360deg); }
        }
        @keyframes pulse-opacity {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.6; }
        }
        @keyframes pulse-scale {
          0%, 100% { transform: scale(0.9); }
          50% { transform: scale(1); }
        }
        @keyframes bounce-dot {
          0%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-6px); }
        }
      `}</style>
    </>
  );
};

export default Spinner;
